/**
 * Admin → Users — pure data logic (no React, no network).
 *
 * One home for what the old UserManagement/UserForm/api.saveUserSettings
 * trio each re-implemented: orgAccess normalisation, role rules, report
 * groups ↔ legacy Power BI grants, department "all" semantics, dirty
 * counting, validation, list filters ↔ URL/query mapping.
 *
 * Backend twin for the server-side rules: azfApi/fastify/routes/auth/userAdminCore.js
 */

export const ROLES = [
  { value: 'orgAdmin', label: 'Org admin', help: 'Can edit data and settings for every location in this organization.' },
  { value: 'orgViewer', label: 'Org viewer', help: 'Can view every location in this organization, but not change anything.' },
  { value: 'locationAdmin', label: 'Location admin', help: 'Can edit data and settings for the locations below only.' },
  { value: 'locationViewer', label: 'Location viewer', help: 'Can view the locations below only.' },
];
export const ROLE_LABELS = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));
export const LODGEIC_ADMIN_FILTER = 'lodgeicAdmin';
export const ROLE_FILTER_OPTIONS = [
  ...ROLES.map((r) => ({ value: r.value, label: r.label })),
  { value: LODGEIC_ADMIN_FILTER, label: 'Lodgeic admin' },
];

const ORG_WIDE = new Set(['orgAdmin', 'orgViewer']);
const LOCATION_ROLES = new Set(['locationAdmin', 'locationViewer']);
export const isOrgWideRole = (role) => ORG_WIDE.has(role);
export const isLocationRole = (role) => LOCATION_ROLES.has(role);

// Native report groups (orgAccess[code].nativeReports). 'forecasting' is the
// explicit Budgeting & Forecasting grant — shown as "Budget + Forecast"; it has no
// Power BI counterpart.
export const REPORT_GROUPS = [
  { value: 'operations', label: 'Operations', code: 'OPS', blurb: 'Labor Management, Guest Reviews' },
  { value: 'revenue', label: 'Revenue', code: 'REV', blurb: 'Revenue, Revenue Forecast, Night Audits' },
  { value: 'financial', label: 'Financial', code: 'FIN', blurb: 'Profit & Loss, Financial Trends, GL Detail' },
  { value: 'forecasting', label: 'Budget + Forecast', code: 'B+F', blurb: 'Budgets, forecast workspace and forecast editing' },
];
const REPORT_GROUP_VALUES = REPORT_GROUPS.map((g) => g.value);
export const PBI_REPORT_GROUPS = ['financial', 'operations', 'revenue'];

// Job-title picker, grouped the way a hotel group is organised: corporate
// office, then property leadership and department heads. Titles not listed
// here stay editable through "Other" (ProfileSection), so existing users keep
// whatever free-text title they already have.
export const JOB_TITLE_GROUPS = [
  {
    label: 'Executive',
    titles: ['CEO', 'President', 'COO', 'CFO', 'Chief Commercial Officer', 'Chief Accounting Officer'],
  },
  {
    label: 'Corporate',
    titles: [
      'VP Operations',
      'VP Finance',
      'VP Accounting',
      'VP Commercial',
      'VP Revenue Management',
      'VP Sales & Marketing',
      'VP Human Resources',
      'Regional Director of Operations',
      'Corporate Controller',
      'Corp Director of Revenue Management',
      'Corp Director of Sales & Marketing',
      'Corporate Chef',
      'Asset Manager',
    ],
  },
  {
    label: 'Property leadership',
    titles: ['General Manager', 'Assistant General Manager', 'Hotel Manager', 'Director of Operations'],
  },
  {
    label: 'Finance & accounting',
    titles: ['Director of Finance', 'Controller', 'Assistant Controller', 'Accounting Manager', 'Accounting & HR Manager', 'Staff Accountant'],
  },
  {
    label: 'Department heads',
    titles: [
      'Director of Rooms',
      'Front Office Manager',
      'Director of Housekeeping',
      'Director of F&B',
      'Executive Chef',
      'Director of Engineering',
      'Director of Sales & Marketing',
      'Director of Sales',
      'Revenue Manager',
      'Director of Human Resources',
    ],
  },
];

export const JOB_TITLES = [...JOB_TITLE_GROUPS.flatMap((g) => g.titles), 'Other'];

// Journal records permission — persisted ONLY when explicitly set; Default
// removes the key so the legacy PostHog journal-export flag governs.
export const JOURNAL_PERMISSIONS = [
  { value: '', label: 'Default — feature flag controlled' },
  { value: 'write', label: 'Write — create, edit and delete' },
  { value: 'edit', label: 'Edit — modify existing only' },
  { value: 'read', label: 'Read — view and export only' },
];
const JOURNAL_VALUES = new Set(['read', 'edit', 'write']);

export const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'deactivated', label: 'Deactivated' },
];

export const PAGE_SIZES = [25, 50, 100];
export const DEFAULT_PAGE_SIZE = 50;

// ── Small predicates ──────────────────────────────────────────────────────

export const isTruthyFlag = (v) => v === 1 || v === true || v === '1' || v === 'true';
export const isDeactivated = (user) => isTruthyFlag(user?.isDeleted);
export const isPlatformAdmin = (user) => isTruthyFlag(user?.isAdmin);
export const isLodgeicStaffEmail = (email) => /@lodgeic\.io$/i.test(String(email || '').trim());

const E164 = /^\+[1-9]\d{6,14}$/;
export const normalizePhone = (value) => {
  const s = String(value ?? '').trim().replace(/[\s().-]/g, '');
  return s || '';
};
export const isE164 = (value) => E164.test(normalizePhone(value));
export const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

export const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'Uppercase letter (A–Z)', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'Lowercase letter (a–z)', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'Number (0–9)', test: (p) => /[0-9]/.test(p) },
  { key: 'special', label: 'Special character (!@#$…)', test: (p) => /[^A-Za-z0-9]/.test(p) },
];
export const isValidPassword = (p) => PASSWORD_RULES.every((r) => r.test(String(p || '')));

// ── Status ────────────────────────────────────────────────────────────────

/**
 * deactivated — isDeleted
 * invited     — invite recorded (invitedAt) and no sign-in since
 * active      — everything else (legacy users without tracking included)
 * The server computes the same thing into adminMeta.status.
 */
export function userStatus(user) {
  if (!user) return 'active';
  if (isDeactivated(user)) return 'deactivated';
  if (!user.lastSignInAt && (user.invitedAt || user.lastInvitedAt)) return 'invited';
  return 'active';
}

export const STATUS_PILL = {
  active: { tone: 'ok', label: 'Active' },
  invited: { tone: 'info', label: 'Invited · not signed in' },
  deactivated: { tone: 'off', label: 'Deactivated' },
};

// ── orgAccess normalisation (the ONE copy) ────────────────────────────────

const arr = (v) => (Array.isArray(v) ? v : []);

/**
 * Normalise one orgAccess entry without dropping unknown keys: arrays exist,
 * legacy report/dashboard/location shapes are reconciled, journalPermission
 * and nativeReports are kept only when explicitly (validly) set.
 */
export function normalizeOrgEntry(entry) {
  const src = entry && typeof entry === 'object' ? entry : {};
  const out = {
    ...src,
    role: src.role || '',
    reportIds: src.reportIds && typeof src.reportIds === 'object' && !Array.isArray(src.reportIds) ? { ...src.reportIds } : {},
    dashboardIds: [...arr(src.dashboardIds)],
    entityIds: [...arr(src.entityIds)],
    locationIds: [...arr(src.locationIds)],
    departmentIds: [...arr(src.departmentIds)],
    chartOfAccountDepartmentIds: [...arr(src.chartOfAccountDepartmentIds)],
    dashboards: [...arr(src.dashboards)],
    reports: [...arr(src.reports)],
    entities: [...arr(src.entities)],
    locations: [...arr(src.locations)],
  };
  delete out.journalPermission;
  delete out.nativeReports;
  if (JOURNAL_VALUES.has(src.journalPermission)) out.journalPermission = src.journalPermission;
  if (Array.isArray(src.nativeReports)) {
    out.nativeReports = [...new Set(src.nativeReports.map((g) => String(g).toLowerCase()))].filter((g) => REPORT_GROUP_VALUES.includes(g));
  }
  out.reports.forEach((id) => { if (!out.reportIds[id]) out.reportIds[id] = { Pages: [] }; });
  out.dashboards.forEach((id) => { if (!out.dashboardIds.includes(id)) out.dashboardIds.push(id); });
  out.entities.forEach((id) => { if (!out.entityIds.includes(id)) out.entityIds.push(id); });
  out.locations.forEach((loc) => {
    const id = loc && typeof loc === 'object' ? loc.id : loc;
    if (id !== undefined && id !== null && !out.locationIds.some((x) => String(x) === String(id))) out.locationIds.push(id);
  });
  return out;
}

export function normalizeOrgAccess(orgAccess) {
  const out = {};
  Object.entries(orgAccess && typeof orgAccess === 'object' ? orgAccess : {}).forEach(([code, entry]) => {
    out[code] = normalizeOrgEntry(entry);
  });
  return out;
}

export function blankOrgEntry(role = '') {
  return normalizeOrgEntry({ role });
}

// ── Draft ─────────────────────────────────────────────────────────────────

export const DRAFT_FIELDS = ['firstName', 'lastName', 'email', 'jobTitle', 'phoneNumber', 'isAdmin', 'orgAccess'];

export function draftFromUser(user) {
  const u = user || {};
  return {
    id: u.id || null,
    email: String(u.email || '').trim(),
    firstName: u.firstName || '',
    lastName: u.lastName || '',
    jobTitle: u.jobTitle || '',
    phoneNumber: u.phoneNumber || '',
    isAdmin: isPlatformAdmin(u) ? 1 : 0,
    orgAccess: normalizeOrgAccess(u.orgAccess),
  };
}

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Unsaved-change count: one per edited profile field, one per added/removed/edited organization. */
export function countDraftChanges(baseline, draft) {
  if (!baseline || !draft) return 0;
  let n = 0;
  DRAFT_FIELDS.forEach((k) => {
    if (k === 'orgAccess') return;
    const a = k === 'phoneNumber' ? normalizePhone(baseline[k]) : baseline[k];
    const b = k === 'phoneNumber' ? normalizePhone(draft[k]) : draft[k];
    if (!same(a ?? '', b ?? '')) n += 1;
  });
  const codes = new Set([...Object.keys(baseline.orgAccess || {}), ...Object.keys(draft.orgAccess || {})]);
  codes.forEach((c) => { if (!same(baseline.orgAccess?.[c], draft.orgAccess?.[c])) n += 1; });
  return n;
}

/**
 * Save payload. New users: the whole draft (POST). Existing users: id + only
 * the changed fields (PATCH merges server-side, so untouched keys — prefs,
 * photos, tags — can never be wiped from here). Email is never sent as a
 * change for an existing user.
 */
export function buildSavePayload(baseline, draft, { isNew } = {}) {
  const orgAccess = normalizeOrgAccess(draft.orgAccess);
  if (isNew) {
    return {
      email: String(draft.email || '').trim().toLowerCase(),
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
      jobTitle: (draft.jobTitle || '').trim(),
      phoneNumber: normalizePhone(draft.phoneNumber) || null,
      isAdmin: draft.isAdmin ? 1 : 0,
      orgAccess,
    };
  }
  const payload = { id: baseline.id, email: baseline.email };
  if (draft.firstName !== baseline.firstName) payload.firstName = draft.firstName.trim();
  if (draft.lastName !== baseline.lastName) payload.lastName = draft.lastName.trim();
  if ((draft.jobTitle || '') !== (baseline.jobTitle || '')) payload.jobTitle = (draft.jobTitle || '').trim();
  if (normalizePhone(draft.phoneNumber) !== normalizePhone(baseline.phoneNumber)) payload.phoneNumber = normalizePhone(draft.phoneNumber) || null;
  if (Number(draft.isAdmin) !== Number(baseline.isAdmin)) payload.isAdmin = draft.isAdmin ? 1 : 0;
  if (!same(normalizeOrgAccess(baseline.orgAccess), orgAccess)) payload.orgAccess = orgAccess;
  return payload;
}

/** Codes whose report-group selection changed (their legacy PBI grants need re-deriving). */
export function changedReportGroupOrgs(baseline, draft) {
  return Object.keys(draft.orgAccess || {}).filter((c) => {
    const next = draft.orgAccess[c]?.nativeReports;
    return Array.isArray(next) && !same(baseline.orgAccess?.[c]?.nativeReports, next);
  });
}

/**
 * Validation. Keys: firstName, lastName, email, phoneNumber, orgAccess,
 * `role:<code>`. Returned in on-screen order so the first key is the one to
 * focus. `manageable` limits org checks to orgs the admin can edit.
 */
export function validateDraft(draft, { isNew, manageable } = {}) {
  const errors = {};
  if (!draft.firstName.trim()) errors.firstName = 'First name is required.';
  if (!draft.lastName.trim()) errors.lastName = 'Last name is required.';
  if (isNew) {
    if (!String(draft.email || '').trim()) errors.email = 'Email is required.';
    else if (!isValidEmail(draft.email)) errors.email = 'Enter a valid email address.';
  }
  if (normalizePhone(draft.phoneNumber) && !isE164(draft.phoneNumber)) {
    errors.phoneNumber = 'Use international format, e.g. +15551234567.';
  }
  const codes = Object.keys(draft.orgAccess || {});
  if (!codes.length) errors.orgAccess = 'Add at least one organization.';
  codes.forEach((code) => {
    if (manageable && !manageable.has(code)) return;
    if (!draft.orgAccess[code]?.role) errors[`role:${code}`] = 'Choose a role.';
  });
  return errors;
}

export const ACCESS_ERROR_KEYS = (errors) => Object.keys(errors).filter((k) => k === 'orgAccess' || k.startsWith('role:'));
export const PROFILE_ERROR_KEYS = (errors) => Object.keys(errors).filter((k) => ['firstName', 'lastName', 'email', 'phoneNumber'].includes(k));

// ── Role + department rules ───────────────────────────────────────────────

/**
 * Role change: any change resets GL (chart-of-account) departments to all;
 * org-wide roles also clear locations and labor departments. Same rule as
 * the server bulk endpoint (userAdminCore.applyRoleChange).
 */
export function applyRoleChange(entry, role) {
  const current = normalizeOrgEntry(entry);
  if (current.role === role) return current;
  const next = { ...current, role, chartOfAccountDepartmentIds: [] };
  if (isOrgWideRole(role)) {
    next.locationIds = [];
    next.departmentIds = [];
  }
  return next;
}

/** Stored department ids → "all"? ([] or anything containing -1, exactly as the backend reads it.) */
export const isAllDepartments = (ids) => !Array.isArray(ids) || ids.length === 0 || ids.some((id) => Number(id) === -1);

const sameId = (a, b) => String(a) === String(b);
const hasId = (list, id) => list.some((x) => sameId(x, id));

/** Is every id of a group selected under the current stored value? */
export function groupState(ids, groupIds) {
  if (isAllDepartments(ids)) return 'all';
  const n = groupIds.filter((id) => hasId(ids, id)).length;
  if (n === 0) return 'none';
  return n === groupIds.length ? 'all' : 'some';
}

/**
 * Toggle a group of department ids (one department, a same-name group or an
 * account-type group). Starting from "all" deselects just that group.
 * Selecting everything collapses back to [] (= all). Deselecting the last
 * selected department is refused (returns the input) — an empty list would
 * mean "all", the opposite of what was clicked.
 */
export function toggleDepartmentGroup(ids, groupIds, allIds) {
  const universe = [...new Set(allIds)];
  const current = isAllDepartments(ids) ? universe : ids.filter((id) => Number(id) !== -1);
  const on = groupIds.every((id) => hasId(current, id));
  const next = on
    ? current.filter((id) => !hasId(groupIds, id))
    : [...current, ...groupIds.filter((id) => !hasId(current, id))];
  if (next.length === 0) return ids;
  if (universe.length && universe.every((id) => hasId(next, id))) return [];
  return next;
}

/** Summary line for a department selection. */
export function departmentSummary(ids, departments, { max = 3 } = {}) {
  if (isAllDepartments(ids)) return 'All departments';
  const names = [...new Set((departments || []).filter((d) => hasId(ids, d.id)).map((d) => d.name || String(d.id)))];
  if (!names.length) return `${ids.length} department${ids.length === 1 ? '' : 's'}`;
  return names.length > max ? `${names.slice(0, max).join(', ')} +${names.length - max} more` : names.join(', ');
}

/** Labor departments grouped by name (the same department exists per location). */
export function groupDepartmentsByName(departments) {
  const map = new Map();
  (departments || []).forEach((d) => {
    const name = d.name || String(d.id);
    if (!map.has(name)) map.set(name, []);
    map.get(name).push(d.id);
  });
  return [...map.entries()]
    .sort(([a], [b]) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map(([name, ids]) => ({ key: name, label: name, ids }));
}

/** GL departments grouped by account type: Revenue, Expense, then others A–Z. */
export function groupCoaDepartments(departments) {
  const map = new Map();
  (departments || []).forEach((d) => {
    const type = d.accountType || 'Other';
    if (!map.has(type)) map.set(type, []);
    map.get(type).push(d);
  });
  const order = { Revenue: 0, Expense: 1 };
  return [...map.entries()]
    .sort(([a], [b]) => (order[a] ?? 2) - (order[b] ?? 2) || a.localeCompare(b))
    .map(([type, list]) => ({
      key: type,
      label: type,
      ids: list.map((d) => d.id),
      items: list
        .slice()
        .sort((x, y) => String(x.name || '').toLowerCase().localeCompare(String(y.name || '').toLowerCase()))
        .map((d) => ({ key: String(d.id), label: d.name || String(d.id), ids: [d.id] })),
    }));
}

// ── Report groups ↔ legacy Power BI grants ────────────────────────────────

/** Mirror of backend nativeReportAccess.groupForPbiReportName. */
export function groupForPbiReportName(name) {
  const key = String(name || '').trim().toLowerCase();
  if (!key) return null;
  if (key.includes('financial')) return 'financial';
  if (key.includes('operations') || key.includes('labor')) return 'operations';
  if (key.includes('revenue')) return 'revenue';
  return null;
}

/**
 * Groups the switches show for one org: the explicit assignment, else what
 * the server resolved from Power BI grants (adminMeta.reportGroups[code]:
 * array, or null = unrestricted legacy grants → the three PBI groups).
 */
export function effectiveReportGroups(entry, resolved) {
  if (Array.isArray(entry?.nativeReports)) return entry.nativeReports;
  if (resolved === null) return PBI_REPORT_GROUPS.slice();
  if (Array.isArray(resolved)) return resolved;
  return [];
}

/** Toggle one group; the first toggle materialises the effective set as explicit. */
export function toggleReportGroup(entry, group, resolved) {
  const current = effectiveReportGroups(entry, resolved);
  const next = current.includes(group) ? current.filter((g) => g !== group) : [...current, group];
  const ordered = REPORT_GROUP_VALUES.filter((g) => next.includes(g));
  return { ...entry, nativeReports: ordered };
}

/**
 * Re-derive the legacy PBI fields from the explicit groups (old UserForm
 * behaviour): named reports follow their group; unclassified reports and all
 * dashboards are granted when any REPORT group (not Budget + Forecast) is on; grants
 * the workspace doesn't list ride along.
 *   pbiReports [{id,name}] | null, pbiDashboards [{id}] | null
 */
export function syncLegacyPbiGrants(entry, pbiReports, pbiDashboards) {
  const groups = Array.isArray(entry.nativeReports) ? entry.nativeReports : [];
  const anyAccess = groups.some((g) => PBI_REPORT_GROUPS.includes(g));
  const available = Array.isArray(pbiReports) ? pbiReports : [];
  const known = new Set(available.map((r) => r.id));
  const reports = anyAccess
    ? [...new Set([
        ...arr(entry.reports).filter((id) => !known.has(id)),
        ...available.filter((r) => {
          const g = groupForPbiReportName(r.name);
          return g ? groups.includes(g) : true;
        }).map((r) => r.id),
      ])]
    : [];
  const reportIds = {};
  reports.forEach((id) => { reportIds[id] = entry.reportIds?.[id] || { Pages: [] }; });
  const dashboards = anyAccess
    ? [...new Set([...arr(entry.dashboards), ...(Array.isArray(pbiDashboards) ? pbiDashboards : []).map((d) => d.id)])]
    : [];
  return { ...entry, reports, reportIds, dashboards, dashboardIds: dashboards };
}

// ── Copy access ───────────────────────────────────────────────────────────

/**
 * Copy another user's access into the draft for the orgs the admin manages;
 * orgs the admin can't manage keep whatever the draft already had.
 */
export function copyAccessFrom(draftOrgAccess, sourceOrgAccess, manageable) {
  const out = {};
  Object.entries(draftOrgAccess || {}).forEach(([code, entry]) => {
    if (manageable && !manageable.has(code)) out[code] = entry;
  });
  Object.entries(normalizeOrgAccess(sourceOrgAccess)).forEach(([code, entry]) => {
    if (!manageable || manageable.has(code)) out[code] = entry;
  });
  return out;
}

// ── Table cells ───────────────────────────────────────────────────────────

/** [{code,name,role}] for the Organizations · role column. */
export function orgRoleChips(user, orgNameByCode = {}) {
  const meta = user?.adminMeta?.orgs;
  const list = Array.isArray(meta)
    ? meta.map((o) => ({ code: o.code, name: orgNameByCode[o.code] || o.name || o.code, role: o.role }))
    : Object.entries(user?.orgAccess || {}).map(([code, e]) => ({ code, name: orgNameByCode[code] || code, role: e?.role }));
  return list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

export function locationAccessLabel(user) {
  const entries = Object.values(user?.orgAccess || {});
  if (!entries.length) return '—';
  const orgWide = entries.filter((e) => isOrgWideRole(e?.role)).length;
  const ids = new Set();
  entries.filter((e) => !isOrgWideRole(e?.role)).forEach((e) => arr(e?.locationIds).forEach((id) => ids.add(String(id))));
  const n = ids.size;
  const locs = `${n} location${n === 1 ? '' : 's'}`;
  if (orgWide === entries.length) return 'All locations';
  if (!orgWide) return n ? locs : 'No locations';
  return `All in ${orgWide} org${orgWide === 1 ? '' : 's'} · ${locs}`;
}

/** Compact report codes (OPS REV FIN B+F) across the user's orgs. */
export function reportCodes(user) {
  const resolved = user?.adminMeta?.reportGroups || {};
  const set = new Set();
  Object.entries(user?.orgAccess || {}).forEach(([code, entry]) => {
    effectiveReportGroups(entry, Object.prototype.hasOwnProperty.call(resolved, code) ? resolved[code] : undefined)
      .forEach((g) => set.add(g));
  });
  return REPORT_GROUPS.filter((g) => set.has(g.value)).map((g) => g.code).join(' ');
}

export function shortDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}

export function longDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export const displayName = (user) => `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || '';

// ── Filters ↔ URL ↔ API query ─────────────────────────────────────────────

const splitParam = (v) => (v ? String(v).split(',').map((s) => s.trim()).filter(Boolean) : []);

export function filtersFromSearchParams(sp) {
  const get = (k) => (typeof sp.get === 'function' ? sp.get(k) : sp[k]);
  const status = STATUS_OPTIONS.some((o) => o.value === get('status')) ? get('status') : 'all';
  const size = Number(get('size'));
  const page = Number(get('page'));
  return {
    q: get('q') || '',
    status,
    orgs: splitParam(get('org')),
    roles: splitParam(get('role')).filter((r) => ROLE_FILTER_OPTIONS.some((o) => o.value === r)),
    reports: splitParam(get('reports')).filter((g) => REPORT_GROUP_VALUES.includes(g)),
    page: Number.isInteger(page) && page > 0 ? page : 1,
    size: PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE,
  };
}

/** Only non-default values go in the URL. */
export function filtersToSearchParams(f) {
  const out = {};
  if (f.q) out.q = f.q;
  if (f.status && f.status !== 'all') out.status = f.status;
  if (f.orgs?.length) out.org = f.orgs.join(',');
  if (f.roles?.length) out.role = f.roles.join(',');
  if (f.reports?.length) out.reports = f.reports.join(',');
  if (f.page && f.page > 1) out.page = String(f.page);
  if (f.size && f.size !== DEFAULT_PAGE_SIZE) out.size = String(f.size);
  return out;
}

/** GET /userSettings admin-list query. `all: true` = every match (export). */
export function filtersToQuery(f, { all = false } = {}) {
  const q = { status: f.status || 'all' };
  if (f.q && f.q.trim()) q.search = f.q.trim();
  if (f.orgs?.length) q.orgs = f.orgs.join(',');
  if (f.roles?.length) q.roles = f.roles.join(',');
  if (f.reports?.length) q.reportGroups = f.reports.join(',');
  if (all) {
    q.limit = 0;
  } else {
    q.limit = f.size || DEFAULT_PAGE_SIZE;
    q.offset = ((f.page || 1) - 1) * q.limit;
  }
  return q;
}

export const hasActiveFilters = (f) => Boolean((f.q && f.q.trim()) || f.orgs?.length || f.roles?.length || f.reports?.length || (f.status && f.status !== 'all'));

export function showingLabel(page, size, total) {
  if (!total) return 'Showing 0 of 0';
  const start = (page - 1) * size + 1;
  const end = Math.min(page * size, total);
  return `Showing ${start}–${end} of ${total}`;
}
